// One stock rule everywhere (FEEDBACK_PLAN contract §1, Q1/Q3/Q12/Q21).
//
// Every stock-bearing API call sends the whole basket ({variant_id: qty} from /cart.js). The engine answers with
// copies_total / in_basket / copies_available per listing (and summed per printing / card). Until (or if) the
// engine omits a field, normalise() derives it here from the same basket, so the UI never shows stock that is
// already in the shopper's basket as available. Engine values always win.
import { cart, vnum } from './singles-hub--cart.js?v=c7c57de095';

// {variant_id: qty} over ALL basket lines (decks and loose items)
export function basketMap(c) {
  const out = {};
  const cc = c || cart.last();
  (cc ? cc.items : []).forEach((l) => { out[l.variant_id] = (out[l.variant_id] || 0) + l.qty; });
  return out;
}
// Compact form for GET query strings: "vid:qty,vid:qty" (sorted, so the GET cache key is stable)
export function basketParam(c) {
  const m = basketMap(c);
  const ks = Object.keys(m).filter((k) => m[k] > 0).sort();
  return ks.length ? ks.map((k) => `${k}:${m[k]}`).join(',') : undefined;
}

const n0 = (x) => (Number.isFinite(+x) ? +x : 0);

// One listing -> {copies_total, in_basket, copies_available}
export function normListing(l, bm) {
  if (!l || l.__mcc_norm) return l;
  const total = l.copies_total != null ? n0(l.copies_total) : n0(l.stock);
  const inB = l.in_basket != null ? n0(l.in_basket) : n0(bm[vnum(l.variant_id)]);
  const avail = l.copies_available != null ? n0(l.copies_available) : Math.max(0, total - inB);
  return Object.assign(l, { copies_total: total, in_basket: inB, copies_available: avail, __mcc_norm: true });
}

// A card summary / card sheet (search result, staple, /card/{id}) in place.
// After this: printing.copies_available / in_basket, card.copies_available / in_basket, cheapest.copies_available,
// playset.fulfillable_qty, and printings ordered in stock first (then in your basket, then sold out).
export function normCard(c, bm) {
  if (!c || c.__mcc_norm) return c;
  bm = bm || basketMap();
  let tot = 0, inB = 0, avail = 0;
  (c.printings || []).forEach((p) => {
    const singles = (p.listings || []).filter((l) => !l.is_playset);
    (p.listings || []).forEach((l) => normListing(l, bm));
    const pt = p.copies_total != null ? n0(p.copies_total) : singles.reduce((a, l) => a + l.copies_total, 0);
    const pb = p.in_basket != null ? n0(p.in_basket) : singles.reduce((a, l) => a + l.in_basket, 0);
    const pa = p.copies_available != null ? n0(p.copies_available) : singles.reduce((a, l) => a + l.copies_available, 0);
    Object.assign(p, { copies_total: pt, in_basket: pb, copies_available: pa });
    tot += pt; inB += pb; avail += pa;
  });
  if (c.copies_total == null) c.copies_total = c.printings ? tot : n0(c.copies_in_stock);
  if (c.in_basket == null) c.in_basket = inB;
  if (c.copies_available == null) c.copies_available = c.printings ? avail : Math.max(0, n0(c.copies_in_stock) - inB);
  if (c.printings) {
    const rank = (p) => (p.copies_available > 0 ? 0 : p.in_basket > 0 ? 1 : 2);
    c.printings = c.printings.map((p, i) => [p, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map((x) => x[0]);
  }
  // cheapest: re-point at the cheapest listing that still has copies available
  const avl = availableListings(c);
  if (avl.length) {
    const l = avl[0];
    const sameAsEngine = c.cheapest && vnum(c.cheapest.variant_id) === vnum(l.variant_id);
    c.cheapest = Object.assign({}, sameAsEngine ? c.cheapest : {}, { variant_id: l.variant_id, price: l.price, printing_id: l.printing_id, label: l.label, finish: l.finish, stock: l.copies_available, copies_available: l.copies_available });
  } else if (c.cheapest) c.cheapest = Object.assign({}, c.cheapest, { stock: 0, copies_available: 0 });
  // playsets: fulfillable_qty <= 4 from what is really available
  if (c.playset || avl.length) {
    const pl = c.playset || {};
    const ful = pl.fulfillable_qty != null ? Math.min(4, n0(pl.fulfillable_qty)) : Math.min(4, avl.reduce((a, l) => a + l.copies_available, 0));
    const productSet = pl.kind === 'playset_product' && pl.variant_id ? normListing(findListing(c, pl.variant_id) || { variant_id: pl.variant_id, stock: 1 }, bm) : null;
    c.playset = Object.assign({}, pl, { fulfillable_qty: productSet && productSet.copies_available > 0 ? 4 : ful });
  }
  c.__mcc_norm = true;
  return c;
}
function findListing(c, vid) {
  for (const p of c.printings || []) for (const l of p.listings || []) if (vnum(l.variant_id) === vnum(vid)) return l;
  return null;
}
// In-stock single listings (not -4X products), cheapest first, with the printing label attached.
export function availableListings(c, printingId) {
  return (c.printings || []).filter((p) => !printingId || p.printing_id === printingId)
    .flatMap((p) => (p.listings || []).filter((l) => !l.is_playset && n0(l.copies_available != null ? l.copies_available : l.stock) > 0)
      .map((l) => Object.assign({}, l, { label: l.label || p.label, printing_id: l.printing_id || p.printing_id })))
    .sort((a, b) => a.price - b.price);
}
// Pence-exact price of the n cheapest available copies (FEEDBACK_PLAN §5)
export function priceOf(listings, n) {
  let left = n, pence = 0;
  listings.forEach((l) => { const k = Math.min(n0(l.copies_available != null ? l.copies_available : l.stock), left); pence += k * Math.round(l.price * 100); left -= k; });
  return pence / 100;
}
// Picks for n copies: [{variant_id, qty}]
export function picksFor(listings, n) {
  let left = n; const out = [];
  listings.forEach((l) => { if (left <= 0) return; const k = Math.min(n0(l.copies_available), left); if (k > 0) { out.push({ variant_id: l.variant_id, qty: k }); left -= k; } });
  return out;
}
export const normAll = (arr, bm) => { bm = bm || basketMap(); (arr || []).forEach((c) => normCard(c, bm)); return arr; };
